import { z } from 'zod';
import { AgentRunStatusSchema, LlmPurposeSchema, WorkerStageSchema } from './enums';
import { AssessmentIdSchema, LlmCallIdSchema, RunIdSchema, TenantIdSchema } from './ids';
import { NonEmptyStringSchema, Sha256Schema, TimestampSchema } from './primitives';
import { ModelIdSchema, ProviderIdSchema } from './model-policy';

/**
 * What a model call cost, and who to attribute it to
 * (docs/DECISIONS.md ZD-046).
 *
 * **Cost telemetry from the outset, not retrofitted**, for the reason ZD-012
 * gives about the analysis plan: the seam that is cheap to build before there
 * are callers is expensive to thread through them afterwards. Every field here
 * exists because something has to be answerable — per call, per assessment, per
 * tenant, and per *worker*, which is the one a margin question actually needs
 * (§13.3: "usage limits … must be calibrated against measured per-run cost").
 *
 * **There is no money column, and that is deliberate.** The fact is the token
 * count; the price is a versioned lookup (`model-policy.ts`) and money is a view
 * over the two. A stored currency figure is arithmetic that cannot be recomputed
 * when a rate changes — ZD-035 §3's prose-arithmetic failure, in a column.
 *
 * Distinct from `AgentRun`, which is one *worker run*: a run may make several
 * calls, and attributing cost to the run alone would lose which call was
 * expensive.
 */
export const LlmCallRecordSchema = z.object({
  llmcall_id: LlmCallIdSchema,
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,
  /** The worker run this call belongs to — several calls may share one. */
  run_id: RunIdSchema,
  worker_name: NonEmptyStringSchema,
  stage: WorkerStageSchema,
  purpose: LlmPurposeSchema,

  provider: ProviderIdSchema,
  model: ModelIdSchema,
  /**
   * The exact model the provider says answered, which need not be the one asked
   * for. Recorded rather than assumed, because "which model version said this"
   * is the client-facing question when a re-issued report disagrees with the
   * original, and `Provenance.asserted_by_run_id` resolves to here to answer it.
   */
  model_version: NonEmptyStringSchema,
  /**
   * What was **applied**, not what was configured: `null` where the model does
   * not accept a temperature. Recording the tenant's pinned 0.2 against a model
   * that rejected the parameter would be a false record of the one value
   * DESIGN_SPEC §12.2's reproducibility story rests on.
   */
  temperature: z.number().nullable(),
  /** Bumped when the prompt changes; part of the determinism cache key. */
  prompt_version: NonEmptyStringSchema,
  /** Bumped when redaction changes; part of the key for the same reason. */
  redaction_version: NonEmptyStringSchema,

  input_tokens: z.int().nonnegative(),
  output_tokens: z.int().nonnegative(),
  cache_read_input_tokens: z.int().nonnegative(),
  cache_write_input_tokens: z.int().nonnegative(),

  /**
   * Whether the **determinism** cache answered (ZD-046 §3) — not the provider's
   * prompt cache, which is a cost mechanism and is counted in the two token
   * columns above. A `hit` records a call that was never made, with zero tokens,
   * so a spend total is a total of calls that actually happened.
   */
  cache: z.enum(['hit', 'miss', 'bypass']),

  status: AgentRunStatusSchema,
  /** Populated whenever status is `rejected` — a schema failure, a refusal. */
  rejection_reason: z.string().nullable(),

  started_at: TimestampSchema,
  ended_at: TimestampSchema,
});
export type LlmCallRecord = z.infer<typeof LlmCallRecordSchema>;

/**
 * The index row for one cached completion (ZD-046 §3).
 *
 * Queryable metadata in D1, payload in tenant-encrypted R2 — the shape
 * `document_indexes` and `extractions` already use, because a cached extraction
 * *is* a client's architecture document restated and ZD-008's scope rule puts
 * that in R2 rather than a D1 column.
 *
 * **No minted id.** The row is addressed by its own cache key, which is a digest
 * of every input that can change the answer: stable, meaningful and already
 * unique. A minted id would be a second handle for a row that has a perfectly
 * good one — ZD-032's reasoning for mapping rows, unchanged.
 */
export const LlmCacheEntrySchema = z.object({
  cache_key: Sha256Schema,
  tenant_id: TenantIdSchema,
  /**
   * Which assessment first produced it. Recorded for retention sweeps and for
   * reading a hit's provenance, and deliberately **not** part of the key — a
   * re-run of the same artefact in a new assessment should hit, which is the
   * whole "you issued a report in March, re-run it in June" case.
   */
  assessment_id: AssessmentIdSchema,
  purpose: LlmPurposeSchema,
  model: ModelIdSchema,
  model_version: NonEmptyStringSchema,
  prompt_version: NonEmptyStringSchema,
  redaction_version: NonEmptyStringSchema,
  storage_ref: NonEmptyStringSchema,
  sha256: Sha256Schema,
  created_at: TimestampSchema,
});
export type LlmCacheEntry = z.infer<typeof LlmCacheEntrySchema>;
